/** @format */
/** @format */

import React, { FC, useState, useEffect } from 'react'
import styled from 'styled-components'
import { Select, Option, Table, TableColumn, Pagination, Tag, Message } from '@king-design/react'
import { MonitorSearch, DeleteConfirm, TablePage, EllipsisTwoLines } from 'src/components'
import { PrometheusAlarmTabProps } from 'src/pages/PrometheusMonitor/Alarm'
import { PageProps, initPage } from 'src/utils/interface'
import { reciverScheme } from 'src/pages/PrometheusMonitor/Alarm/constants'
import { useHistory } from 'react-router-dom'
import { useAsyncFn } from 'react-use'
import { getPrometheusAlerting, getPrometheusAlertPolicyList } from 'src/api/ApiPrometheusAlarm'

interface SearchProps extends PageProps {
  Instance: string
  PolicyName: string
}

const initSearch: SearchProps = {
  Instance: '',
  PolicyName: '',
  ...initPage
}

const PrometheusAlarming: FC<PrometheusAlarmTabProps> = ({ className, region, instances }) => {
  const history = useHistory()
  const [search, setSearch] = useState<SearchProps>(initSearch)

  const [{ value: tableData }, fetch] = useAsyncFn(
    async (params?) => {
      const result = await getPrometheusAlerting({
        ...search,
        ...params,
        Region: region
      })
      return {
        data: result?.Records || [],
        total: result?.TotalCount || 0
      }
    },
    [search, region]
  )

  useEffect(() => {
    fetch()
  }, [search, region])

  const toPolicyPage = async (policyID, ruleName?) => {
    if (policyID) {
      const result = await getPrometheusAlertPolicyList({
        PolicyID: parseInt(policyID)
      })
      if (result?.Polices?.length) {
        let url = `/monitor/prometheusmonitor/alarm/edit?tab=alarming&policyid=${policyID}`
        if (ruleName) {
          url += `&rulename=${ruleName}`
        }
        history.push(url)
      } else {
        Message.error('策略不存在')
      }
    } else {
      Message.error('策略ID为空')
    }
  }

  return (
    <div className={className}>
      <div className="search_row">
        <div className="search_item">
          <p className="search_item_label">关联资源</p>
          <Select
            value={search.Instance}
            clearable
            filterable
            onChangeValue={value => {
              setSearch({
                ...search,
                Page: 1,
                Instance: value
              })
            }}
          >
            {instances?.map(item => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </div>
        <div className="search_item">
          <p className="search_item_label">策略名称</p>
          <MonitorSearch
            placeholder="请输入策略名称，支持模糊搜索"
            onChange={value => {
              setSearch({
                ...search,
                Page: 1,
                PolicyName: value
              })
            }}
          />
        </div>
      </div>
      <Table
        data={tableData?.data || []}
        animation={false}
        rowKey={data => data.ID}
        checkType="none"
      >
        <TableColumn
          key="InstanceID"
          title="关联资源"
          slotTemplate={([data]) => {
            return data.InstanceID ? (
              <>
                <p>{data.InstanceName}</p>
                <p className="ellipsis_two_lines table_text">{data.InstanceID}</p>
              </>
            ) : null
          }}
        ></TableColumn>
        <TableColumn
          key="RuleName"
          title="策略 / 规则"
          slotTemplate={([data]) => {
            return (
              <a
                onClick={() => {
                  toPolicyPage(data.PolicyID, data.RuleName)
                }}
              >{`${data.PolicyName} / ${data.RuleName}`}</a>
            )
          }}
        />
        <TableColumn key="CreatedAt" title="触发时间" width="160"></TableColumn>
        <TableColumn
          key="AlertStatus"
          title="告警状态"
          width="100"
          slotTemplate={([data]) => {
            return data.AlertStatus === 'firing' ? (
              <Tag type="danger">告警中</Tag>
            ) : (
              <Tag type="default">已恢复</Tag>
            )
          }}
        ></TableColumn>
        <TableColumn
          key="Summary"
          title="告警摘要"
          slotTemplate={([data]) => {
            return <EllipsisTwoLines title="告警摘要">{data.Summary}</EllipsisTwoLines>
          }}
        ></TableColumn>
        <TableColumn
          key="Description"
          title="告警内容"
          slotTemplate={([data]) => {
            return <EllipsisTwoLines title="告警内容">{data.Description}</EllipsisTwoLines>
          }}
        ></TableColumn>
        <TableColumn
          key="Recipients"
          title="通知对象"
          width="120"
          slotTemplate={([data]) => {
            return data.Recipients?.length || data.WebHooks?.length ? (
              <div className="ksc-space">
                {data.Recipients ? (
                  <DeleteConfirm
                    title="联系人"
                    content={
                      <>
                        <TablePage
                          style={{ marginTop: 0 }}
                          tableProps={{
                            checkType: 'none',
                            rowKey: data => data.ContactID,
                            scheme: reciverScheme,
                            data: data.Recipients
                          }}
                        ></TablePage>
                      </>
                    }
                  >
                    <a>联系人</a>
                  </DeleteConfirm>
                ) : null}
                {data.WebHooks ? (
                  <DeleteConfirm
                    title="回调"
                    content={
                      <>
                        {data.WebHooks.map((url, index) => (
                          <p style={{ margin: '4px 0' }} key={url + index}>
                            {url}
                          </p>
                        ))}
                      </>
                    }
                  >
                    <a>回调</a>
                  </DeleteConfirm>
                ) : null}
              </div>
            ) : (
              '-'
            )
          }}
        ></TableColumn>
      </Table>
      <Pagination
        value={search.Page}
        limit={search.PageSize}
        total={tableData?.total || 0}
        size="small"
        onChangeValue={value => {
          setSearch({
            ...search,
            Page: value
          })
        }}
        onChangeLimit={limit => {
          setSearch({
            ...search,
            PageSize: limit
          })
        }}
      />
    </div>
  )
}

export default styled(PrometheusAlarming)``
